import React from 'react';
import clsx from 'clsx';

import { Button, Checkbox, Text } from '@auspost/postmaster-react';

import CrossIcon from 'assets/cross-thin.svg';
import TrashIcon from 'assets/trash.svg';
import { SelectedShipment } from 'models/SelectRemove';
import { selectIsAuthed } from 'store/customer';
import { selectAll, toggleEditMode } from 'store/selectRemove/selectRemove.slice';
import { selectShipments } from 'store/shipment/shipment.selectors';
import { useAppDispatch, useAppSelector } from 'store/store';

import SelectAndRemoveModal from './SelectAndRemoveModal/SelectAndRemoveModal';
import {
  REMOVE_BUTTON_TEST_ID,
  REMOVE_CANCEL_BUTTON_TEST_ID,
  SELECT_AND_REMOVE_BUTTON_TEST_ID,
  SELECT_AND_REMOVE_TEST_ID,
} from './SelectRemove.test-ids';

import styles from './SelectRemove.module.scss';

const SelectRemove: React.FC<{ className?: string }> = ({ className }) => {
  const dispatch = useAppDispatch();
  const shipmentList = useAppSelector(selectShipments);
  const isAuth = useAppSelector(selectIsAuthed) ?? false;
  const selectedShipments = useAppSelector((state) => state.selectRemove).selectedShipments;
  const toggleEditModeCheck = useAppSelector((state) => state.selectRemove).editMode;
  const [editMode, setEditMode] = React.useState(false);
  const [isSelectAll, setIsSelectAll] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  React.useEffect(() => {
    const allSelected = selectedShipments?.every((shipment) => shipment.checked);
    setIsSelectAll(!!allSelected);
  }, [selectedShipments]);

  // Close editMode when deleting articles
  React.useEffect(() => {
    if (!toggleEditModeCheck) setEditMode(false);
  }, [toggleEditModeCheck]);

  const getCheckedItems = () => {
    const checked = selectedShipments?.filter((item) => item.checked);
    if (!checked) return null;
    if (checked?.length === 1) {
      const { consignmentId, nickname, trackingId, isMultiArticleConsignment } = checked[0];
      if (isMultiArticleConsignment) return `'${nickname ?? consignmentId}'`;
      return `'${nickname ?? trackingId}'`;
    } else if (checked?.length > 1) {
      return `${checked.length} parcels`;
    }
  };
  const checkedItems = getCheckedItems();

  // Toggle edit mode
  const handleEditMode = () => {
    setEditMode((prevEditMode) => !prevEditMode);

    if (editMode) {
      dispatch(toggleEditMode({ editMode: false }));
      return setIsSelectAll(false);
    }

    const shipments: SelectedShipment[] = [];
    shipmentList.forEach((shipment) => {
      if (shipment.articles && shipment.articles.length > 0) {
        const { articleId, nickname } = shipment.articles[0];
        shipments.push({
          trackingId: articleId,
          nickname,
          checked: false,
          consignmentId: shipment.consignmentId,
          isMultiArticleConsignment: (shipment.articleCount ?? 1) > 1 || shipment.articles.length > 1,
        });
      }
    });

    dispatch(toggleEditMode({ editMode: true, shipments }));
  };

  // Select or deselect all
  const handleSelectAll = () => {
    setIsSelectAll((prevEditMode) => !prevEditMode);
    dispatch(selectAll(!isSelectAll));
  };

  const displaySelectRemove = (
    <>
      <Text variant='body' weight='medium'>
        {shipmentList.length} {shipmentList.length === 1 ? 'parcel' : 'parcels'}
      </Text>
      {isAuth && shipmentList.length > 0 && (
        <Button
          size='medium'
          variant='tertiary-filled'
          onClick={handleEditMode}
          data-testid={SELECT_AND_REMOVE_BUTTON_TEST_ID}
          className={styles.selectAndRemoveBtn}
        >
          <TrashIcon />
          Select & remove
        </Button>
      )}
    </>
  );

  const displayEditMode = (
    <>
      <Checkbox
        isChecked={isSelectAll}
        onChange={handleSelectAll}
        id='select-all-shipments-checkbox'
        labelledBy='select-all-shipments-checkbox'
        label={isSelectAll ? 'Deselect All' : 'Select All'}
      />
      {checkedItems && (
        <>
          <Button
            size='medium'
            variant='tertiary-filled'
            className={styles.removeBtn}
            onClick={() => setIsModalOpen(!isModalOpen)}
            data-testid={REMOVE_BUTTON_TEST_ID}
          >
            <TrashIcon />
            Remove
          </Button>
          <SelectAndRemoveModal
            isOpen={isModalOpen}
            handleClose={() => setIsModalOpen(false)}
            checkedItems={checkedItems}
          />
        </>
      )}
      <Button
        size='medium'
        variant='tertiary-filled'
        className={styles.cancelBtn}
        onClick={handleEditMode}
        data-testid={REMOVE_CANCEL_BUTTON_TEST_ID}
      >
        <CrossIcon />
      </Button>
    </>
  );

  return (
    <div className={clsx(styles.selectRemove, className)} data-testid={SELECT_AND_REMOVE_TEST_ID}>
      {!editMode ? displaySelectRemove : displayEditMode}
    </div>
  );
};

export default SelectRemove;
